import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Icon } from '../components/Icons';
import '../styles/Login.css';

/* ── Google SVG ── */
const GoogleIcon = () => (
  <svg className="google-icon" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
    <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
  </svg>
);

/* ── Microsoft icon ── */
const MicrosoftIcon = () => (
  <div className="ms-icon">
    <span style={{ background: '#F25022' }} />
    <span style={{ background: '#7FBA00' }} />
    <span style={{ background: '#00A4EF' }} />
    <span style={{ background: '#FFB900' }} />
  </div>
);

/* ── Inline SVG illustration — laptop + shield + plant + bars (matches screenshot) ── */
const LoginIllustration = () => (
  <svg viewBox="0 0 520 260" xmlns="http://www.w3.org/2000/svg"
    style={{ width: '100%', maxWidth: 520, height: 'auto' }}
    aria-hidden="true"
  >
    {/* ── bar charts left-back ── */}
    <rect x="28"  y="170" width="22" height="60"  rx="4" fill="#BFDBFE" opacity="0.7"/>
    <rect x="56"  y="148" width="22" height="82"  rx="4" fill="#93C5FD" opacity="0.8"/>
    <rect x="84"  y="162" width="22" height="68"  rx="4" fill="#BFDBFE" opacity="0.7"/>

    {/* ── bar charts right-back ── */}
    <rect x="420" y="158" width="20" height="72"  rx="4" fill="#93C5FD" opacity="0.8"/>
    <rect x="446" y="172" width="20" height="58"  rx="4" fill="#BFDBFE" opacity="0.7"/>
    <rect x="472" y="145" width="20" height="85"  rx="4" fill="#7DC3FA" opacity="0.75"/>

    {/* ── floating checklist card (left) ── */}
    <rect x="30" y="60" width="120" height="90" rx="12" fill="white" opacity="0.85"
      style={{ filter: 'drop-shadow(0 4px 12px rgba(37,99,235,0.12))' }}/>
    {/* check rows */}
    <circle cx="50" cy="88"  r="8" fill="#BFDBFE"/>
    <polyline points="46,88 49,91 55,85" stroke="#2563EB" strokeWidth="2" fill="none" strokeLinecap="round"/>
    <rect x="65" y="84" width="66" height="5"  rx="2" fill="#DBEAFE"/>
    <rect x="65" y="93" width="50" height="4"  rx="2" fill="#E0EEFF"/>

    <circle cx="50" cy="115" r="8" fill="#BFDBFE"/>
    <polyline points="46,115 49,118 55,112" stroke="#2563EB" strokeWidth="2" fill="none" strokeLinecap="round"/>
    <rect x="65" y="111" width="58" height="5"  rx="2" fill="#DBEAFE"/>
    <rect x="65" y="120" width="44" height="4"  rx="2" fill="#E0EEFF"/>

    {/* ── laptop base ── */}
    <rect x="148" y="90" width="224" height="148" rx="14"
      fill="#D6E8FF" style={{ filter: 'drop-shadow(0 8px 28px rgba(37,99,235,0.18))' }}/>
    {/* screen bezel */}
    <rect x="158" y="98" width="204" height="128" rx="8" fill="#EFF6FF"/>
    {/* fingerprint on screen */}
    <circle cx="260" cy="162" r="36" fill="none" stroke="#93C5FD" strokeWidth="3.5" opacity="0.5"/>
    <circle cx="260" cy="162" r="26" fill="none" stroke="#60A5FA" strokeWidth="3"   opacity="0.65"/>
    <circle cx="260" cy="162" r="16" fill="none" stroke="#3B82F6" strokeWidth="2.5" opacity="0.8"/>
    <circle cx="260" cy="162" r="7"  fill="none" stroke="#2563EB" strokeWidth="2"   opacity="0.9"/>
    {/* arcs for fingerprint lines */}
    <path d="M238,148 Q260,132 282,148" fill="none" stroke="#60A5FA" strokeWidth="2.2" opacity="0.7" strokeLinecap="round"/>
    <path d="M234,158 Q260,138 286,158" fill="none" stroke="#93C5FD" strokeWidth="2"   opacity="0.6" strokeLinecap="round"/>
    <path d="M235,170 Q260,148 285,170" fill="none" stroke="#93C5FD" strokeWidth="2"   opacity="0.5" strokeLinecap="round"/>
    {/* laptop base/hinge */}
    <rect x="136" y="236" width="248" height="12" rx="6" fill="#BFDBFE" opacity="0.8"/>
    <rect x="196" y="248" width="128" height="6"  rx="3" fill="#93C5FD" opacity="0.7"/>

    {/* ── shield (floating above laptop) ── */}
    <path d="M308,20 L348,36 L348,72 Q348,92 308,108 Q268,92 268,72 L268,36 Z"
      fill="url(#shieldGrad)"
      style={{ filter: 'drop-shadow(0 8px 20px rgba(37,99,235,0.35))' }}/>
    {/* padlock body */}
    <rect x="295" y="62" width="26" height="22" rx="5" fill="white" opacity="0.9"/>
    {/* padlock shackle */}
    <path d="M300,62 L300,54 Q308,46 316,54 L316,62" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" opacity="0.9"/>
    {/* keyhole */}
    <circle cx="308" cy="72" r="4" fill="#2563EB" opacity="0.7"/>
    <rect x="306" y="73" width="4" height="6" rx="1" fill="#2563EB" opacity="0.7"/>
    <defs>
      <linearGradient id="shieldGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%"   stopColor="#1D4ED8"/>
        <stop offset="100%" stopColor="#3B82F6"/>
      </linearGradient>
    </defs>

    {/* ── plant (bottom left) ── */}
    {/* pot */}
    <path d="M58,230 L42,260 L74,260 Z" fill="#93C5FD" opacity="0.7"/>
    <rect x="44" y="248" width="28" height="12" rx="3" fill="#BFDBFE" opacity="0.8"/>
    {/* stem */}
    <line x1="58" y1="230" x2="58" y2="200" stroke="#7DC3FA" strokeWidth="3" strokeLinecap="round"/>
    {/* leaves */}
    <ellipse cx="45" cy="210" rx="14" ry="9" fill="#93C5FD" opacity="0.8" transform="rotate(-30,45,210)"/>
    <ellipse cx="71" cy="205" rx="14" ry="9" fill="#7DC3FA" opacity="0.8" transform="rotate(30,71,205)"/>
    <ellipse cx="50" cy="220" rx="12" ry="7" fill="#BFDBFE" opacity="0.7" transform="rotate(-15,50,220)"/>

    {/* small circles decoration */}
    <circle cx="440" cy="55"  r="7" fill="#BFDBFE" opacity="0.6"/>
    <circle cx="460" cy="42"  r="4" fill="#93C5FD" opacity="0.5"/>
    <circle cx="420" cy="48"  r="5" fill="#DBEAFE" opacity="0.55"/>
  </svg>
);

const FEATURES = [
  { cls:'blue',   iconColor:'#2563EB', icon:'Calendar',    title:'Weekly timesheet submission',   desc:'Submit your timesheet in just a few clicks' },
  { cls:'green',  iconColor:'#10B981', icon:'CheckCircle', title:'Instant approval workflows',    desc:'Get real-time updates on approvals' },
  { cls:'purple', iconColor:'#8B5CF6', icon:'BarChart',    title:'Real-time analytics & reports', desc:'Track productivity with powerful insights' },
  { cls:'amber',  iconColor:'#F59E0B', icon:'Umbrella',    title:'Leave management built-in',     desc:'Apply, track and manage leaves easily' },
];

const Login = () => {
  const [username,     setUsername]     = useState('');
  const [password,     setPassword]     = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe,   setRememberMe]   = useState(false);
  const [error,        setError]        = useState('');
  const [loading,      setLoading]      = useState(false);
  const { login } = useAuth();
  const navigate  = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    // Client-side validations
    if (!username.trim()) { setError('Username is required.'); return; }
    if (!password.trim()) { setError('Password is required.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }

    setLoading(true);
    const result = await login(username, password, rememberMe);
    setLoading(false);
    if (result?.success) {
      navigate('/home');
    } else {
      setError(result?.message || 'Invalid username or password. Please try again.');
    }
  };

  return (
    <div className="login-container">

      {/* ══ LEFT — info + illustration ══ */}
      <motion.div className="login-left"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.45 }}
      >
        <div className="login-left-decoration" aria-hidden="true" />
        <div className="login-left-blob"        aria-hidden="true" />

        <div className="login-left-content">
          <div className="login-brand">
            <motion.h1
              initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }}
              transition={{ delay:0.1, duration:0.4 }}
            >
              WorkForce<br/>Management
            </motion.h1>
            <motion.p
              initial={{ opacity:0 }} animate={{ opacity:1 }}
              transition={{ delay:0.2, duration:0.38 }}
            >
              Track, submit, and manage timesheets<br/>effortlessly — all in one place.
            </motion.p>
          </div>

          <div className="login-features">
            {FEATURES.map((f, i) => (
              <motion.div key={f.title} className="login-feature-item"
                initial={{ opacity:0, x:-16 }} animate={{ opacity:1, x:0 }}
                transition={{ delay: 0.28 + i*0.08, duration:0.36 }}
              >
                <div className={`lf-icon ${f.cls}`}>
                  {f.icon==='Calendar'    && <Icon.Calendar    size={17} color={f.iconColor}/>}
                  {f.icon==='CheckCircle' && <Icon.CheckCircle size={17} color={f.iconColor}/>}
                  {f.icon==='BarChart'    && <Icon.BarChart    size={17} color={f.iconColor}/>}
                  {f.icon==='Umbrella'    && <Icon.Umbrella    size={17} color={f.iconColor}/>}
                </div>
                <div className="lf-text">
                  <h4>{f.title}</h4>
                  <p>{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Illustration pinned to bottom */}
        <div className="login-illustration">
          <LoginIllustration />
        </div>
      </motion.div>

      {/* ══ RIGHT — sign-in card ══ */}
      <div className="login-right">
        <motion.div className="login-card"
          initial={{ opacity:0, y:24 }} animate={{ opacity:1, y:0 }}
          transition={{ duration:0.42, ease:[0.4,0,0.2,1] }}
        >
          <div className="login-card-header">
            <h2>Welcome back!</h2>
            <p>Sign in to continue to your account</p>
          </div>

          <form onSubmit={handleLogin} noValidate>
            <div className="login-input-group">
              <div className="login-field">
                <label className="login-label" htmlFor="login-username">Username</label>
                <div className="login-input-wrap">
                  <span className="login-input-icon"><Icon.User size={15} color="#94A3B8"/></span>
                  <input id="login-username" type="text"
                    className={`login-input ${error ? 'input-error' : ''}`}
                    placeholder="Enter your username"
                    value={username} onChange={e => setUsername(e.target.value)}
                    autoComplete="username" required
                  />
                </div>
              </div>

              <div className="login-field">
                <label className="login-label" htmlFor="login-password">Password</label>
                <div className="login-input-wrap">
                  <span className="login-input-icon"><Icon.Lock size={15} color="#94A3B8"/></span>
                  <input id="login-password" type={showPassword ? 'text' : 'password'}
                    className={`login-input ${error ? 'input-error' : ''}`}
                    placeholder="Enter your password"
                    value={password} onChange={e => setPassword(e.target.value)}
                    autoComplete="current-password" required
                  />
                  <button type="button" className="login-eye-btn" tabIndex={-1}
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}>
                    {showPassword ? <Icon.EyeOff size={15} color="#94A3B8"/> : <Icon.Eye size={15} color="#94A3B8"/>}
                  </button>
                </div>
              </div>
            </div>

            <div className="login-row">
              <label className="login-remember">
                <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)}/>
                Remember me
              </label>
              <Link to="/forgotPassword" className="login-forgot">Forgot Password?</Link>
            </div>

            {error && (
              <motion.div className="login-error"
                initial={{ opacity:0, y:-6 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.2 }}>
                <Icon.AlertCircle size={13} color="#EF4444"/> {error}
              </motion.div>
            )}

            <motion.button type="submit" className="login-button" disabled={loading}
              whileHover={{ scale:1.01 }} whileTap={{ scale:0.99 }}>
              {loading
                ? <span style={{ display:'inline-block', width:15, height:15, border:'2px solid rgba(255,255,255,0.4)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin 0.7s linear infinite' }}/>
                : <>Sign in <Icon.ArrowRight size={16} color="#fff"/></>
              }
            </motion.button>
          </form>



          <div className="auth-links-container">
            Don't have an account?{' '}
            <Link to="/signup" className="auth-link">Sign up</Link>
          </div>
        </motion.div>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default Login;
